// ─────────────────────────────────────────────────────────────────────────────
// Los dos avisos del armazón de la maqueta: «Métricas unificadas» y «Auditoría activa», más la línea
// «Mostrando…» que dice, en una frase, sobre qué se está mirando.
//
// 🔴 Los dos avisos afirman algo, así que solo se pintan si es CIERTO en esta pantalla:
//
//   · «Auditoría activa» se condiciona a `canExportData`. Lo que se audita es la DESCARGA
//     (`registrarExport`, en Líderes y Moderación); a quien no puede exportar, el aviso le prometería
//     un registro que no va a existir y, peor, le sugeriría que su lectura queda fichada.
//   · «Métricas unificadas» se mudó a `BannerHomologacion`, que la maqueta pone justo debajo de las
//     pestañas. Aquí quedaría duplicada.
//
// La línea «Mostrando…» NO repite los tags de filtro de arriba: aquellos son controles removibles
// («Fechas: … ×»), y esto es una frase de lectura. Con los filtros por omisión dice «toda la
// organización» en vez de callar, porque «sin filtros» es también un alcance y conviene verlo escrito
// antes de leer una cifra.
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';

export interface RecorteMostradoProps {
  /** Nombre de la OU del filtro, ya resuelto. Vacío o ausente = toda la organización. */
  readonly ouName?: string | null;
  /** Rango del filtro, ya formateado. Ausente = sin acotar. */
  readonly rango?: string | null;
  /** Fundamental del filtro, ya resuelto a nombre. */
  readonly fundamentalName?: string | null;
}

/**
 * «Mostrando: OU · rango · Fundamental». 2026-09-29 (documento de Adriana) — sube a la parte de arriba
 * de la tarjeta de filtros y con un color que se vea más, «para que el usuario identifique
 * rápidamente el filtro aplicado». Antes era una línea gris al final de los banners.
 */
export function RecorteMostrado({ ouName, rango, fundamentalName }: RecorteMostradoProps) {
  const { t } = useTranslation('recognition');
  const partes = [
    ouName?.trim() ? ouName : t('reports.context.wholeOrg'),
    rango?.trim() ? rango : null,
    fundamentalName?.trim() ? fundamentalName : null,
  ].filter(Boolean) as string[];

  return (
    <p className="rr-context-showing rr-context-showing--destacado" role="status" data-testid="recorte-mostrado">
      <i className="fas fa-filter" aria-hidden="true" />
      <span>
        {t('reports.context.showing')} <strong>{partes.join(' · ')}</strong>
      </span>
    </p>
  );
}

export interface BannersDeContextoProps {
  /**
   * Si esta persona puede descargar. Decide si el aviso de auditoría es cierto para ella: lo que
   * queda registrado es la descarga, no la consulta.
   */
  readonly canExportData: boolean;
}

export function BannersDeContexto({ canExportData }: BannersDeContextoProps) {
  const { t } = useTranslation('recognition');

  return (
    <div className="rr-context">
      <div className="rr-context-banners">
        {canExportData && (
          <p className="rr-context-banner rr-context-banner--audit">
            <i className="fas fa-shield-halved" aria-hidden="true" />
            <span>
              <strong>{t('reports.context.auditTitle')}</strong> {t('reports.context.auditText')}
            </span>
          </p>
        )}
      </div>
    </div>
  );
}
